import { z } from 'zod'
import { IS_DURATION_MESSAGE, IS_ISRC_MESSGAE, REQUIRED_MESSAGE } from '@Const/error-messages'
import { isrcInvalidCheck } from '@App/helpers/work-entities-fields-validators'
import { FormFields } from '@App/forms/v2/common/form-field'
import { FormFieldTypes } from '@App/enum/form-field-types'
import populateSchema from '@App/forms/v2/helpers/populate-schema'
import { maxByteLength } from '@Entities/schemas/common/useful-zod-functions'
import { stringSizeMaxMessage } from '@Entities/schemas/work/error-messages'

const titleKey = 'title'
const artistNameKey = 'artistName'
const isrcKey = 'isrc'
const durationKey = 'duration'
const labelKey = 'label'
const releaseDateKey = 'releaseDate'

const addRecordingSchema = z.object({
    [titleKey]: z
        .string({
            error: REQUIRED_MESSAGE,
        })
        .trim()
        .min(1, REQUIRED_MESSAGE)
        .max(200, stringSizeMaxMessage('Title', 200)),
    [artistNameKey]: z
        .string({
            error: REQUIRED_MESSAGE,
        })
        .trim()
        .min(1, REQUIRED_MESSAGE)
        .refine(maxByteLength(60), 'Recording Artist Name is too long'),
    [isrcKey]: z
        .string()
        .refine((isrcVal) => !isrcInvalidCheck(isrcVal), IS_ISRC_MESSGAE)
        .optional(),
    [durationKey]: z
        .string()
        .refine((durationVal) => !durationVal || durationVal.match(/^[0-9]{2}[:]{1}[0-9]{2}$/), IS_DURATION_MESSAGE)
        .optional(),
    [labelKey]: z.string().refine(maxByteLength(60), 'Label is too long').optional(),
    [releaseDateKey]: z.date().optional(),
})

type AddRecordingType = z.infer<typeof addRecordingSchema>

const addRecordingFormFieldsNoSchema: FormFields<AddRecordingType> = {
    [titleKey]: {
        name: titleKey,
        label: 'Recording Title',
        type: FormFieldTypes.TEXT,
    },
    [artistNameKey]: {
        name: artistNameKey,
        label: 'Recording Artist Name',
        type: FormFieldTypes.TEXT,
    },
    [isrcKey]: {
        name: isrcKey,
        label: 'ISRC',
        type: FormFieldTypes.TEXT,
    },
    [labelKey]: {
        name: labelKey,
        label: 'Label',
        type: FormFieldTypes.TEXT,
    },
    [durationKey]: {
        name: durationKey,
        label: 'Duration',
        type: FormFieldTypes.TEXT,
    },
    [releaseDateKey]: {
        name: releaseDateKey,
        label: 'Release Date',
        type: FormFieldTypes.DATEPICKER,
    },
} as const

const addRecordingFormFields: FormFields<AddRecordingType> = populateSchema(
    addRecordingFormFieldsNoSchema,
    addRecordingSchema
)

export { addRecordingFormFields, addRecordingSchema, AddRecordingType }
